/**
 * Primitives shared by every stage of a cloud runtime connection attempt:
 * abort/timeout helpers, the retry policy, progress emission, and the two
 * connection option/descriptor shapes every stage's signature carries.
 *
 * Extracted from `connectCloudRuntimeProvisioning.ts` once ensure/start/
 * connect/register split into their own files — each of those stages used
 * every one of these, so this is the base of the DAG rather than one more
 * sibling. `CloudRuntimeConnectProgressEvent` / `...Reporter` stay declared in
 * `connectCloudRuntime.ts` (six external call sites already import them from
 * that path) and are imported back here type-only, which erases at build time
 * and does not create a runtime cycle.
 */
import { resolveCmmdRuntimeSpriteName } from "@cmmd-forge/contracts";
import { isCmmdAuthFailure, readCmmdAuthFailureCode } from "@cmmd-forge/shared/cmmdAuthFailure";
import {
  CLOUD_RUNTIME_CONNECT_MAX_ATTEMPTS,
  shouldRetryCloudRuntimeResponse,
} from "../../environments/runtime/cloudRuntimeConnectRetry";
import type { CloudRuntimeConnectFailureAttachment } from "../cloudRuntimeTerminalFailureResidual";
import { readCloudRuntimeFailureResidual } from "../cloudRuntimeTerminalFailureResidual";
import type {
  CloudRuntimeConnectProgressEvent,
  CloudRuntimeConnectProgressReporter,
} from "./connectCloudRuntime";

export const CONNECT_MAX_ATTEMPTS = CLOUD_RUNTIME_CONNECT_MAX_ATTEMPTS;

export function createAbortError(): Error {
  const error = new Error("The cloud project connection attempt was cancelled.");
  error.name = "AbortError";
  return error;
}

export function throwIfAborted(signal: AbortSignal): void {
  if (signal.aborted) throw createAbortError();
}

export function sleepWithAbort(ms: number, signal: AbortSignal): Promise<void> {
  throwIfAborted(signal);
  return new Promise<void>((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      window.clearTimeout(timeoutId);
      reject(createAbortError());
    };
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

export interface CloudRuntimeConnectionOptions {
  readonly signal: AbortSignal;
  readonly onProgress?: CloudRuntimeConnectProgressReporter;
}

export interface CloudRuntimeConnectionDescriptor {
  readonly httpBaseUrl: string;
  readonly wsBaseUrl?: string;
  readonly bearerToken: string;
  readonly expiresAt?: string;
}

export function emitConnectProgress(
  onProgress: CloudRuntimeConnectProgressReporter | undefined,
  event: Omit<CloudRuntimeConnectProgressEvent, "at">,
): void {
  onProgress?.({
    ...event,
    at: new Date().toISOString(),
  });
}

/**
 * A Sprite/status blurb shared by the ensure and start responses — both report
 * the same identity + readiness shape from CMMD's dual contract (#5456).
 */
export function formatEnsureRuntimeDetail(runtime: {
  readonly spriteName?: string | null;
  readonly flyMachineId?: string | null;
  readonly flyVolumeId?: string | null;
  readonly readinessPhase?: string | null;
}): string {
  const spriteName = resolveCmmdRuntimeSpriteName(runtime);
  const parts = [
    spriteName ? `Sprite ${spriteName}` : null,
    // Sprites has no separate volume, so this is null in practice today; kept
    // so a substrate that does report one still renders.
    runtime.flyVolumeId ? `volume ${runtime.flyVolumeId}` : null,
    runtime.readinessPhase ? runtime.readinessPhase : null,
  ].filter((part): part is string => part !== null);
  return parts.length > 0 ? parts.join(" · ") : "CMMD returned an ensured runtime.";
}

export function shouldRetryConnectResponse(
  response: Response,
  body: unknown,
  attempt: number,
  maxAttempts: number = CONNECT_MAX_ATTEMPTS,
): boolean {
  // The CMMD auth vocabulary is richer than the bare status code, so it keeps
  // its own short-circuit ahead of the shared policy.
  if (isCmmdAuthFailure(response.status, body)) return false;
  return shouldRetryCloudRuntimeResponse({
    status: response.status,
    body,
    attempt,
    maxAttempts,
  });
}

/**
 * A proxy in front of CMMD gave up waiting on the origin, not CMMD itself.
 *
 * FOR-3579: CMMD ran a workspace seed synchronously inside `/ensure`. The
 * request ran past Cloudflare's 100s edge timeout, and Cloudflare answered
 * 524 with its own HTML page while CMMD's handler kept running unaware. That
 * is "still working," not a refusal, and it must not spend the small fixed
 * attempt budget meant for a genuine 5xx/409 from CMMD's own app layer — a
 * budget that assumes each attempt is cheap, which is false here by
 * construction. 502 and 504 are the same shape from any gateway in front of
 * the origin.
 */
export function isGatewayTimeoutStatus(status: number): boolean {
  return status === 502 || status === 504 || status === 524;
}

/** Human-readable gap for the progress line: "300ms", "2.4s". */
export function formatRetryDelay(delayMs: number): string {
  return delayMs < 1_000 ? `${delayMs}ms` : `${Math.round(delayMs / 100) / 10}s`;
}

export function retryAfterHeader(response: Response): string | null {
  // `Response` is stubbed in unit tests without a `headers` bag; a missing
  // header simply means "use our own backoff".
  return response.headers?.get?.("retry-after") ?? null;
}

/**
 * The `code` CMMD put on a refusal body, or null.
 *
 * Auth codes keep their own typed reader ahead of this one. Every other code is
 * the `forge_runtimes.errorCode` vocabulary (`runtime_boot_failed`,
 * `runtime_busy`, ...), forwarded for the composer to key fixed copy on; the
 * message beside it is never rendered when a code is recognised, because CMMD's
 * own `errorMessage` can name the sprite and quote guest output. Structured
 * residual fields ride on the thrown error instead (`failureResidual`).
 */
export function readCloudRuntimeFailureCode(body: unknown): string | null {
  const authCode = readCmmdAuthFailureCode(body);
  if (authCode) return authCode;
  if (!body || typeof body !== "object") return null;
  const code = (body as { readonly code?: unknown }).code;
  return typeof code === "string" && code.trim().length > 0 ? code : null;
}

export function throwCloudRuntimeConnectFailure(
  response: Response,
  body: unknown,
  attachment: CloudRuntimeConnectFailureAttachment = {},
): never {
  const errBody = body && typeof body === "object" ? (body as { readonly error?: unknown }) : {};
  const fallback =
    response.status === 401 || response.status === 403
      ? "Authentication error. Sign in again and retry."
      : "Could not connect to cloud project. Please try again.";
  const message =
    typeof errBody.error === "string" && errBody.error.trim().length > 0 ? errBody.error : fallback;
  const code = readCloudRuntimeFailureCode(body);
  const failureResidual = readCloudRuntimeFailureResidual(body, attachment);
  throw Object.assign(new Error(message), {
    status: response.status,
    ...(code ? { code } : {}),
    ...(failureResidual ? { failureResidual } : {}),
  });
}
